// Sales Journey (CRM revisions Task 5): the full lifecycle "story" of every lead,
// built PURELY from the single event store (auditLogs) + the lead docs. The
// auditLogs are server-written by onLeadWrite (one row per changed field); the
// "Created" event is synthesized from the lead's own createdAt/createdBy because
// onLeadWrite only fires on update. Pure + unit-tested; UI/Firestore live elsewhere.
import { normalizeStage } from './leadConstants';

// Fields that are noise in a human journey (ownerName rides along with ownerUid;
// bookkeeping fields are already ignored by onLeadWrite and never reach here).
const SKIP_FIELDS = new Set(['ownerName', 'updatedBy', 'updatedByName', 'updatedAt']);

// Human-readable event type for an audit row (field-level change).
export function eventTypeFor(row) {
  const { field, oldValue, newValue } = row;
  switch (field) {
    case 'stage': return 'Status changed';
    case 'ownerUid': return oldValue ? 'Reassigned' : 'Assigned';
    case 'territoryId': return 'Territory changed';
    case 'verified': return newValue === true || newValue === 'true' ? 'Verified' : 'Unverified';
    case 'archived': return newValue === true || newValue === 'true' ? 'Archived' : 'Un-archived';
    case 'leadValue': return 'Value edited';
    case 'coverageDays': return 'Coverage days edited';
    default: return `Field edited (${field})`;
  }
}

// Current status label for a lead (normalized stage + archived flag).
export function currentStatusOf(lead) {
  if (!lead) return '';
  const stage = normalizeStage(lead.stage ?? '');
  return lead.archived ? `${stage} (archived)` : stage;
}

// Build the chronological journey across ALL leads (open / Won / Lost / archived).
// Returns events sorted oldest→newest: { leadId, leadName, ts, actor, eventType,
// oldValue, newValue, currentStatus }.
export function buildJourney(leads = [], auditLogs = []) {
  const leadMap = new Map((leads || []).map((l) => [l.id, l]));
  const events = [];

  // Synthesized "Created" event per lead (onLeadWrite doesn't fire on create).
  for (const l of leads || []) {
    if (l.createdAt == null) continue;
    events.push({
      leadId: l.id,
      leadName: l.businessName ?? '',
      ts: l.createdAt,
      actor: l.createdByName ?? l.createdBy ?? 'system',
      eventType: 'Created',
      oldValue: '',
      newValue: '',
      currentStatus: currentStatusOf(l),
    });
  }

  // Field-level events from the single event store.
  for (const row of auditLogs || []) {
    if (SKIP_FIELDS.has(row.field)) continue;
    const lead = leadMap.get(row.docId);
    events.push({
      leadId: row.docId,
      leadName: lead?.businessName ?? '',
      ts: row.ts,
      actor: row.displayName ?? row.uid ?? 'system',
      eventType: eventTypeFor(row),
      oldValue: row.oldValue == null ? '' : row.oldValue,
      newValue: row.newValue == null ? '' : row.newValue,
      currentStatus: currentStatusOf(lead),
    });
  }

  events.sort((a, b) => (a.ts ?? 0) - (b.ts ?? 0));
  return events;
}

// CSV column spec (order + labels) for the Sales Journey export.
export const JOURNEY_COLUMNS = [
  { key: 'leadId', label: 'Lead ID' },
  { key: 'leadName', label: 'Lead Name' },
  { key: 'tsIso', label: 'Event Timestamp' },
  { key: 'actor', label: 'Actor' },
  { key: 'eventType', label: 'Event Type' },
  { key: 'oldValue', label: 'Old Value' },
  { key: 'newValue', label: 'New Value' },
  { key: 'currentStatus', label: 'Current Status' },
];

// Shape journey events into CSV rows (timestamp → ISO string).
export function journeyToRows(events) {
  return (events || []).map((e) => ({
    ...e,
    tsIso: e.ts ? new Date(e.ts).toISOString() : '',
  }));
}
